import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { emsApi } from '@/api/emsApi';
import { resolveAtePhoneNumber } from '@ngsos/portal-core/incidents';
import { NgButton, NgDataListSelect, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';
import type { EditFormModelValidationResults } from '@/ui/forms/useEditForm';
import { incidentDetailRoute } from '@/features/shared/routes';
import type { GeoPoint } from '@/signalr/contracts';
import { HOSPITALS, hospitalDisplayValue } from './newIncidentHospitals';
import type { HospitalInfo } from './newIncidentHospitals';
import { IncidentCreationFeedback } from './IncidentCreationFeedback';
import { useCreateIncident } from './useCreateIncident';

const FORM_ID = 'new-incident-from-data-sentence-form';

const validateDataSentenceField = (value: string): string[] =>
  value.trim() === '' ? ['The DataSentence field is required.'] : [];

type ParseAteIncidentResponse = {
  type: string;
  position: GeoPoint | null;
  fullName: string;
  age: number | null;
  phoneNumber: string | null;
  transferFrom: { name: string } | null;
  note: string;
  errors: { line: number; message: string }[];
};

export type NewIncidentFromDataSentenceDialogHandle = {
  showModal: () => void;
};

export type NewIncidentFromDataSentenceDialogProps = {
  ref?: Ref<NewIncidentFromDataSentenceDialogHandle>;
};

export function NewIncidentFromDataSentenceDialog({ ref }: NewIncidentFromDataSentenceDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const modalRef = useRef<NgModalHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const creation = useCreateIncident();
  const creating = creation.pending;
  const creationStatus = creation.statusFor('ate');

  const [dataSentence, setDataSentence] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedHospital, setSelectedHospital] = useState<HospitalInfo | undefined>(undefined);
  const editForm = useEditForm<'dataSentence' | 'phoneNumber' | 'selectedHospital'>({
    fieldValidators: { dataSentence: validateDataSentenceField },
  });

  const hospitalSelectDisabled = !dataSentence.includes('SEKUNDÁRNY PLÁNOVANY');

  useImperativeHandle(ref, () => ({
    showModal: () => {
      if (creation.isPending()) return;
      creation.reset();
      editForm.reset({ dataSentence, phoneNumber });
      modalRef.current?.show();
    },
  }));

  const validateModel = (): EditFormModelValidationResults<'dataSentence' | 'phoneNumber' | 'selectedHospital'> => {
    const dataSentenceMessages = validateDataSentenceField(dataSentence);
    if (dataSentenceMessages.length > 0) {
      return { dataSentence: dataSentenceMessages };
    }

    if (!hospitalSelectDisabled && selectedHospital == null) {
      return { selectedHospital: [t('NewIncidentDialog_SelectHospital')] };
    }

    return {};
  };

  const createAteIncident = async (incidentId: string) => {
    const parseResponse = await emsApi.POST('/portal/v1/sk/ate-parse', {
      body: { message: dataSentence },
    });
    if (parseResponse.data === undefined) {
      throw new Error(`Failed to parse the ATE data sentence (HTTP ${parseResponse.response.status})`);
    }

    const ateMessage = parseResponse.data as unknown as ParseAteIncidentResponse;

    const position = ateMessage.position ?? selectedHospital?.point ?? null;

    const createResponse = await emsApi.POST('/portal/v1/sk/ate-incident', {
      body: {
        requestedIncidentId: incidentId,
        position,
        fullName: ateMessage.fullName,
        age: ateMessage.age,
        phoneNumber: resolveAtePhoneNumber(phoneNumber, ateMessage.phoneNumber),
        note: ateMessage.note,
      },
    });
    if (createResponse.response.status >= 400) {
      throw new Error(`Failed to create the ATE incident (HTTP ${createResponse.response.status})`);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editForm.validate(validateModel)) {
      return;
    }

    const incidentId = await creation.create({ key: 'ate', execute: createAteIncident });
    if (incidentId !== null) await navigate(incidentDetailRoute(incidentId));
  };

  return (
    <NgModal
      ref={modalRef}
      disableClosingDialog={creating}
      progressTemplate={
        creationStatus !== 'idle' ? (
          <IncidentCreationFeedback
            status={creationStatus}
            onRetry={() => formRef.current?.requestSubmit()}
            onCancel={() => modalRef.current?.hide()}
          />
        ) : undefined
      }
      header={t('NewIncidentDialog_Header')}
      bodyTemplate={
        <form ref={formRef} id={FORM_ID} onSubmit={(event) => void onSubmit(event)} aria-busy={creating}>
          <fieldset disabled={creating}>
            <div>
              <label htmlFor="data-sentence" className="label">
                {t('NewIncidentFromDataSentenceDialog_Label_DataSentence')}
              </label>
              <textarea
                id="data-sentence"
                className={editForm.fieldCssClass('dataSentence', 'form-control')}
                rows={6}
                value={dataSentence}
                onChange={(event) => {
                  setDataSentence(event.target.value);
                  editForm.notifyFieldInput('dataSentence', event.target.value);
                }}
                onBlur={() => editForm.notifyFieldBlur('dataSentence')}
              />
              {editForm.validationMessages('dataSentence').map((message, index) => (
                <div key={index} className="validation-message">
                  {message}
                </div>
              ))}
            </div>

            <div>
              <label htmlFor="phone-number" className="label">
                {t('NewIncidentDialog_Label_PhoneNumber')}
              </label>
              <input
                type="tel"
                id="phone-number"
                className={editForm.fieldCssClass('phoneNumber', 'form-control')}
                value={phoneNumber}
                onChange={(event) => {
                  setPhoneNumber(event.target.value);
                  editForm.notifyFieldInput('phoneNumber', event.target.value);
                }}
                onBlur={() => editForm.notifyFieldBlur('phoneNumber')}
              />
              {editForm.validationMessages('phoneNumber').map((message, index) => (
                <div key={index} className="validation-message">
                  {message}
                </div>
              ))}
            </div>

            <div>
              <NgDataListSelect
                value={selectedHospital}
                onValueChanged={setSelectedHospital}
                textSelector={hospitalDisplayValue}
                items={HOSPITALS}
                label={t('NewIncidentFromDataSentenceDialog_Label_Hospital')}
                nullText={t('NewIncidentFromDataSentenceDialog_InputPlaceholder_SelectHospital')}
                disabled={creating || hospitalSelectDisabled}
              />
              {editForm.validationMessages('selectedHospital').map((message, index) => (
                <div key={index} className="validation-message">
                  {message}
                </div>
              ))}
            </div>
          </fieldset>
        </form>
      }
      footerTemplate={
        <>
          <NgButton
            disabled={creating}
            submitButton
            form={FORM_ID}
            icon="fal fa-plus-circle"
            text={t('NewIncidentFromDataSentenceDialog_Button_CreateFromDataSentence')}
          />
          <NgButton disabled={creating} text={t('Button_Cancel')} outline onClick={() => modalRef.current?.hide()} />
        </>
      }
    />
  );
}
